import React, { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";

const ProcessStep = ({ step, index, isEven }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: false,
    margin: "-20% 0% -40% 0%",
  });

  return (
    <div
      ref={ref}
      className={`relative flex flex-col md:flex-row items-center w-full ${
        !isEven ? "md:flex-row-reverse" : ""
      }`}
    >
      {/* Timeline Node */}
      <div
        className={`absolute left-0 md:left-1/2 md:-translate-x-1/2 top-8 md:top-1/2 md:-translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white border-[3px] flex items-center justify-center z-10 transition-all duration-700 shadow-md ${
          isInView ? "border-blue-600 scale-125" : "border-gray-100 scale-100"
        }`}
      >
        <div
          className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-colors duration-700 ${
            isInView ? "bg-blue-600" : "bg-blue-50"
          }`}
        >
          <span
            className={`font-bold text-[12px] md:text-sm leading-none transition-colors duration-500 ${
              isInView ? "text-white" : "text-blue-600"
            }`}
          >
            {step.num}
          </span>
        </div>
      </div>

      {/* Content Card */}
      <div
        className={`w-full md:w-1/2 pl-[72px] pr-0 md:px-0 ${
          isEven ? "md:pr-16" : "md:pl-16"
        }`}
      >
        <div
          style={{
            transform: "translateZ(0)",
            willChange: "transform, opacity, box-shadow",
          }}
          className={`bg-white p-8 md:p-10 rounded-[24px] border transition-[transform,opacity,box-shadow,border-color] duration-700 relative overflow-hidden text-left ${
            isInView
              ? "shadow-[0_24px_48px_rgba(37,99,235,0.08)] -translate-y-2 border-blue-300/50"
              : "shadow-[0_4px_24px_rgba(0,0,0,0.03)] translate-y-0 border-gray-100/80"
          }`}
        >
          {/* Huge Number Graphic inside Card */}
          <div
            className={`absolute -bottom-10 text-[130px] md:text-[180px] leading-none font-black select-none transition-all duration-1000 ${
              isEven
                ? "md:-left-10 -right-4 md:right-auto"
                : "-right-4 md:-right-10"
            } ${isInView ? "text-blue-50/80 scale-105 -translate-y-4" : "text-[#cccccc29] scale-100 translate-y-0"}`}
          >
            {step.num}
          </div>

          {/* Card Content */}
          <div
            className={`relative z-10 flex flex-col h-full ${
              isEven ? "md:items-end md:text-right" : "items-start text-left"
            }`}
          >
            {/* Icon Container with tilt on active */}
            <div
              className={`w-12 h-12 md:w-16 md:h-16 border text-xl md:text-3xl rounded-2xl flex justify-center items-center mb-6 transition-all duration-700 ${
                isInView
                  ? "scale-110 rotate-[5deg] bg-white shadow-md border-blue-100/50"
                  : "scale-100 rotate-0 bg-gradient-to-br from-blue-50 to-gray-50 border-blue-100/50"
              }`}
            >
              {step.icon}
            </div>

            <h3
              className={`text-xl md:text-2xl font-bold mb-4 transition-colors duration-500 ${
                isInView ? "text-blue-600" : "text-gray-900"
              }`}
            >
              {step.title}
            </h3>

            <p
              className={`leading-relaxed text-[14px] md:text-[16px] transition-colors duration-500 ${
                isInView ? "text-gray-700" : "text-gray-600"
              }`}
            >
              {step.desc}
            </p>
          </div>
        </div>
      </div>

      {/* Empty Space for the alternating side */}
      <div className="hidden md:block md:w-1/2"></div>
    </div>
  );
};

const ProcessSection = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end center"],
  });

  const scrollHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const steps = [
    {
      num: "01",
      icon: "💬",
      title: "Consultation & Scope",
      desc: "Share your requirements — size, material, finish, and application. Our team advises on the best custom printing solution for your product.",
    },
    {
      num: "02",
      icon: "🎨",
      title: "Design & Digital Proofing",
      desc: "We prepare or refine your artwork, provide a high-res digital proof, and ensure everything is perfect before getting your approval.",
    },
    {
      num: "03",
      icon: "🖨️",
      title: "Precision Printing",
      desc: "Produced on our state-of-the-art presses, ensuring absolute color consistency, sharp text, and premium finishing at high speeds.",
    },
    {
      num: "04",
      icon: "🔍",
      title: "Rigorous Quality Check",
      desc: "Every batch undergoes multi-stage inspection for adhesion, die-cut accuracy, text clarity and color matching before dispatch.",
    },
    {
      num: "05",
      icon: "📦",
      title: "Fast & Secure Delivery",
      desc: "Your labels are securely packed and shipped via our reliable logistics network, ensuring on-time delivery across any location.",
    },
  ];

  return (
    <>
      <section
        id="process"
        ref={containerRef}
        className="py-20 sm:py-24 bg-white relative overflow-hidden"
      >
        {/* === PRINTING THEME BACKGROUND ELEMENTS === */}

        {/* Subtle Halftone Dot Pattern */}
        <div
          className="absolute inset-0 opacity-[0.02] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#000 1.5px, transparent 1.5px)",
            backgroundSize: "32px 32px",
          }}
        ></div>

        {/* CMYK Accent Glows (Cyan, Magenta, Yellow subtle blobs) */}
        <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-cyan-400/5 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-fuchsia-500/5 blur-[150px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[300px] bg-yellow-400/5 blur-[150px] rounded-full pointer-events-none"></div>

        {/* === MAIN CONTENT === */}
        <div className="max-w-[1200px] mx-auto px-6 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30, z: 0 }}
            whileInView={{ opacity: 1, y: 0, z: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ willChange: "transform, opacity" }}
            className="text-center mb-24 relative"
          >
            <div className="text-blue-600 bg-blue-50 border border-blue-600/20 font-black text-xs mb-6 px-5 py-2 w-fit mx-auto rounded-full uppercase tracking-[0.2em] shadow-sm">
              Our Process
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              From Concept to Delivery
            </h2>

            <p className="text-gray-500 max-w-[650px] mx-auto text-[16px] sm:text-lg leading-relaxed font-medium">
              Experience a streamlined, print-perfect journey. We combine modern
              technology with deep expertise to bring your labels to life.
            </p>
          </motion.div>

          {/* Timeline Container */}
          <div className="relative max-w-[1300px] mx-auto">
            {/* Timeline Line: Background (Gray) */}
            <div className="absolute left-[22px] md:left-1/2 md:-translate-x-1/2 top-4 bottom-4 w-[3px] bg-gray-200 opacity-80 rounded-full shadow-[0_0_15px_rgba(0,0,0,0.05)]"></div>

            {/* Timeline Line: Progress (CMYK Gradient) */}
            <motion.div
              style={{
                height: scrollHeight,
                originY: 0,
              }}
              className="absolute left-[22px] md:left-1/2 md:-translate-x-1/2 top-4 w-[3px] bg-gradient-to-b from-cyan-400 via-fuchsia-500 to-yellow-400 opacity-100 rounded-full shadow-[0_0_15px_rgba(0,0,0,0.1)] z-10"
            ></motion.div>

            {/* Steps Grid */}
            <div className="space-y-16 md:space-y-12 pb-10">
              {steps.map((step, index) => (
                <ProcessStep
                  key={index}
                  step={step}
                  index={index}
                  isEven={index % 2 === 0}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProcessSection;
